import moment from "moment";
import { useContext, useEffect, useState, useCallback } from "react";
import Backend from "../../../Backend/Backend";
import toast from "react-hot-toast";
import LivePlMaster from "../../../Utils/LivePlMaster/LivePlMaster";
import { useDispatch, useSelector } from "react-redux";
import Socket from "../../../context/SocketContext";

const fmt = require("indian-number-format");
const backend = await new Backend().init();

export default function ProfitLoss() {
  const dispatch = useDispatch();
  const socket = useContext(Socket);

  const userType = Number(localStorage.getItem("user_type") || 7);
  const totalMaster = useSelector((state) => state.totalmaster || 0);
  const totalAdminPF = useSelector((state) => state.totaladmin_pf || 0);

  const [loading, setLoading] = useState(false);
  const [booked, setBooked] = useState(0);
  const [sharing, setSharing] = useState(0);
  const [standing, setStanding] = useState([]);
  const [credit, setCredit] = useState(0);
  const [isDollar, setisDollar] = useState(0);
  const [isWeeklyInterest, setisWeeklyInterest] = useState(0);

  /** ✅ Load Profit/Loss data */
  const loadProfitLoss = useCallback(async () => {
    try {
      dispatch({ type: "CLEAR_PFMASTER" });
      setLoading(true);

      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        start: moment().startOf("week"),
        end: moment().endOf("week"),
      };

      let res;
      if (userType === 1) res = await backend.profit_loss_admin(data);
      else if (userType === 2) res = await backend.profit_loss_super(data);
      else if (userType === 3) res = await backend.profit_loss_master(data);
      else if (userType === 4) res = await backend.profit_loss(data);
      else if (userType === 5) res = await backend.profit_loss_broker(data);

      setLoading(false);

      if (!res) return;
      if (res.error === "False") {
        setBooked(userType === 4 ? res.profit_loss : res.booked || 0);
        setSharing(res.sharing || 0);
        setStanding(res.standing || []);
        setCredit(res.credit || 0);
        setisDollar(res.isDollar);
        setisWeeklyInterest(res.weekly_interest);
      } else {
        if (res.logout == 1) window.LogOut();
        else toast.error(res.message);
      }
    } catch (err) {
      setLoading(false);
      console.error("Error in loadProfitLoss:", err);
      toast.error("Error loading profit/loss data");
    }
  }, [dispatch, userType]);

  /** ✅ Handle socket reload */
  const handleReload = useCallback(
    (payload) => {
      const id = localStorage.getItem("id");
      const roleKey = {
        1: "admin",
        2: "super",
        3: "master",
        4: "id",
        5: "broker",
        6: "subbroker",
      }[userType];
      if (roleKey && payload[roleKey] && String(payload[roleKey]) === id) {
        console.log("Reload triggered for:", roleKey, id);
        loadProfitLoss();
      }
    },
    [userType, loadProfitLoss]
  );

  /** 🔁 Initial load + socket binding */
  useEffect(() => {
    loadProfitLoss();
  }, [loadProfitLoss]);

  useEffect(() => {
    socket.on("reload", handleReload);
    return () => socket.off("reload", handleReload);
  }, [socket, handleReload]);

  /** 🧮 Computations */
  const netAdminPF = totalMaster < 0 ? -totalAdminPF : totalAdminPF;

  const selfNetAdminPF = parseFloat(totalAdminPF) - parseFloat(sharing) - parseFloat(isWeeklyInterest);

  /** 🧱 UI */
  return (
    <div
      key={loading}
      style={{
        display: "flex",
        fontSize: 12,
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
    >
      {standing.map((i) => (
        <LivePlMaster item={i} key={i.id} total={standing.length} isDollar={isDollar} />
      ))}

      {isWeeklyInterest > 0 ? (
        <PLItem
          label="INTEREST"
          value={parseFloat(isWeeklyInterest || 0).toFixed(0)}
          isDollar={isDollar}
        />
      ) : null}

      <PLItem
        label="BOOKED"
        value={parseFloat(booked || 0).toFixed(0)}
        isDollar={isDollar}
      />
      {userType === 4 ? (
        <PLItem
          label="CREDIT"
          value={parseFloat(credit || 0).toFixed(0)}
          isDollar={isDollar}
        />
      ) : null}
      {userType === 4 ? null : (
        <PLItem
          label="SELF-BOOKED"
          value={parseFloat(sharing || 0).toFixed(0)}
          isDollar={isDollar}
        />
      )}
      {userType !== 4 ? (
        <PLItem label="P/L" value={totalMaster} isDollar={isDollar} />
      ) : (
        <PLItemUser value={totalMaster} isDollar={isDollar} />
      )}

      {userType === 4 ? null : (
        <PLItemSelf
          label="SELF P/L"
          value={parseFloat(netAdminPF || 0).toFixed(2)}
          colorvalue={parseFloat(totalAdminPF || 0).toFixed(2)}
          isDollar={isDollar}
        />
      )}

      {userType === 4 ? null : (
        <div
          className={`text large white ${selfNetAdminPF <= 0 ? "buy" : "sell"}`}
          style={{
            marginRight: 10,
            padding: "0 10px",
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
          }}
        >
          NET {selfNetAdminPF <= 0 ? "PROFIT" : "LOSS"} :
          <div className="extralarge" style={{ paddingLeft: 5 }}>
            {isDollar === 0 ? fmt.format(Math.abs(selfNetAdminPF).toFixed(0)) : fmt.format(Math.abs(selfNetAdminPF).toFixed(2))}{" "}
            {isDollar === 1 ? "$" : ""}
          </div>
        </div>
      )}
    </div>
  );
}

/** ✅ Small subcomponent for repeated UI pattern */
function PLItem({ label, value, isDollar }) {
  return (
    <div
      className="font-bold"
      style={{
        marginRight: 15,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        className={
          label === "INTEREST"
            ? "selltext"
            : value >= 0
              ? "buytext"
              : "selltext"
        }
        style={{ fontSize: 14 }}
      >
        {`${isDollar === 0 ? fmt.format(Math.abs(value).toFixed(0)) : fmt.format(Math.abs(value).toFixed(2))} ${isDollar === 1 ? "$" : ""}`}
      </div>
      <div style={{ fontSize: 9 }}>{label}</div>
    </div>
  );
}

function PLItemUser({ value, isDollar }) {
  return (
    <div
      className={`text large white ${value > 0 ? "buy" : "sell"}`}
      style={{
        marginRight: 10,
        padding: "0 10px",
        borderRadius: 4,
        display: "flex",
        alignItems: "center",
      }}
    >
      NET {value > 0 ? "PROFIT" : "LOSS"} :
      <div className="extralarge" style={{ paddingLeft: 5 }}>
        {isDollar === 0 ? fmt.format(Math.abs(value).toFixed(0)) : fmt.format(Math.abs(value).toFixed(2))} {isDollar === 1 ? "$" : ""}
      </div>
    </div>
  );
}

function PLItemSelf({ label, value, colorvalue, isDollar }) {
  return (
    <div
      className="font-bold"
      style={{
        marginRight: 15,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        className={colorvalue >= 0 ? "selltext" : "buytext"}
        style={{ fontSize: 14 }}
      >
        {`${isDollar === 0 ? fmt.format(Math.abs(value || 0).toFixed(0)) : fmt.format(Math.abs(value || 0).toFixed(2))} ${isDollar === 1 ? "$" : ""}`}
      </div>
      <div style={{ fontSize: 9 }}>{label}</div>
    </div>
  );
}
